/* eslint-disable no-undef */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { UriBuilder } from '../../helpers/UriBuilder';
import MapAPI from '../../api/mapApi';
import { COUNTRY } from '../../constants/crux';
import Colors from '../../constants/colors';
import { ESRI } from '../../constants/map';
import Commons from '../../helpers/Commons';

const MAP_PARAMS = {
    SPATIAL_REF: 4326,
    AU: {
        LAYER_PATH: '/geometry/propertyOverlay/propertySelect',
    },
    NZ: {
        LAYER_PATH: '/overlays/propertyParcel/mapServer/0/query',
    },
};

const UPPER_CASE_COUNTRY = COUNTRY.toUpperCase();
const LIMIT = 2000;
const usePropertyBoundaryLayer = (props) => {
    const offset = useRef(0);
    const responses = useRef(null);
    const prevPolyRef = useRef(null);
    const removeDataLayer = () => {
        if (props.overlayOptions?.latestOnly === undefined
        || props.overlayOptions?.latestOnly) {
            props.map?.data?.forEach(layer => props.map?.data?.remove(layer))
        }
    };
    const listeners = useRef();
    const abortController = useRef();
    // Track whether the last query was a single-coordinate point lookup.
    // When true, the API response is trimmed to the first feature only so that
    // overlapping parcel boundaries at the same lat/lon don't all get drawn.
    // Circle queries also resolve to esriGeometryPoint but must NOT be capped.
    const isSingleCoordinate = useRef(false);

    const esriPropertyBoundaryUrl = new UriBuilder('/query')
        .addParam(ESRI.PARAMS.RESOURCE_URL, MAP_PARAMS[UPPER_CASE_COUNTRY].LAYER_PATH)
        .addParam(ESRI.PARAMS.RESULT_OFFSET, offset.current)
        .addParam(ESRI.PARAMS.RESULT_RECORD_COUNT, LIMIT);

    if (props.overlayOptions?.withPropertyIdOnly === undefined
        || props.overlayOptions?.withPropertyIdOnly) {
        esriPropertyBoundaryUrl.addParam(ESRI.PARAMS.WHERE, 'property_id IS NOT NULL');
    }

    if (ESRI.PARAMS.RETURN_GEOMETRY in (props.overlayOptions ?? {})) {
        esriPropertyBoundaryUrl.addParam(ESRI.PARAMS.RETURN_GEOMETRY, props.overlayOptions.returnGeometry);
    }

    const stashResponse = (geoJson) => {
        if (!responses.current) {
            responses.current = geoJson;
        } else {
            responses.current.features.push(...geoJson.features);
        }
    }

    const fetchMapData = () => {
        removeDataLayer();
        abortController.current = new AbortController();
        MapAPI.getMapLayerImage(esriPropertyBoundaryUrl.build(), {
            signal: abortController.current.signal,
            fetchTimeout: setTimeout(() => abortController.current.abort(), 30000),
        }).then(response => {
            // When the query used a single coordinate (esriGeometryPoint via coordinates[0]),
            // the ESRI service may return several overlapping parcels. Cap to the first
            // feature so we only highlight the most-relevant boundary.
            const processedResponse = (isSingleCoordinate.current && response?.features?.length > 1)
                ? { ...response, features: [response.features[0]] }
                : response;
            stashResponse(processedResponse);
            if (props.overlayOptions?.showPropertyBoundariesOnLoad ||
                props.overlayOptions?.showPropertyBoundariesOnLoad === undefined) {
                props.map?.data?.addGeoJson(processedResponse);
            }
            if (processedResponse.exceededTransferLimit) {
                offset.current += LIMIT;
                esriPropertyBoundaryUrl.params.splice(esriPropertyBoundaryUrl.params
                    .findIndex(param => param.key === ESRI.PARAMS.RESULT_OFFSET), 1, {
                    key: ESRI.PARAMS.RESULT_OFFSET,
                    value: offset.current,
                });
                fetchMapData();
            } else {
                props.overlayOptions?.onFetchSuccess?.(responses.current);
                if ((props.overlayOptions?.showPropertyBoundariesOnLoad ||
                    props.overlayOptions?.showPropertyBoundariesOnLoad === undefined)
                    && !!responses.current?.features?.length) {
                    const bounds = responses.current?.features.reduce((geoJsonBounds, feature) => {
                        feature.geometry?.coordinates?.flat().forEach(coord => {
                            geoJsonBounds.extend({
                                lat: coord[1],
                                lng: coord[0],
                            })
                        });
                        return geoJsonBounds;
                    }, new google.maps.LatLngBounds());
                    // workaround on a weird behaviour (it goes somewhere else)
                    props.map.fitBounds(bounds);
                    props.map.fitBounds(bounds);
                }
                if (props.overlayOptions?.events?.click) {
                    listeners.current?.remove();
                    listeners.current = props.map?.data?.addListener('click', props.overlayOptions.events.click);
                }
                // always clear the response stash and offset to prevent deleted properties from reappearing
                responses.current = null;
                offset.current = 0;
            }
        }).catch((e) => {
            if (e.message !== 'Error: New map query' && e.name !== 'AbortError') {
                props.overlayOptions?.onFetchError?.(e);
            }
            responses.current = null;
            offset.current = 0;
        }); // be weary of using finally. It will trigger before the next recursion.
    };

    const resetLayer = () => {
        removeDataLayer();
        abortController.current?.abort('New map query'); // Abort the request if it exists
    };

    useEffect(() => {
        const hasCoordinates = props.selectOptions?.coordinates?.[0]?.lon && props.selectOptions?.coordinates?.[0]?.lat;
        if (hasCoordinates) {
            if (props.selectOptions.coordinates.length === 1) {
                isSingleCoordinate.current = true;
                esriPropertyBoundaryUrl
                    .addParam(ESRI.PARAMS.GEOMETRY, `${props.selectOptions.coordinates[0].lon},${props.selectOptions.coordinates[0].lat}`)
                    .addParam(ESRI.PARAMS.GEOMETRY_TYPE, ESRI.VALUES.GEOMETRY_TYPE.ESRI_GEOMETRY_POINT);
            } else {
                isSingleCoordinate.current = false;
                const geometry = {
                    points: props.selectOptions.coordinates.map(coord => [coord.lon, coord.lat])
                };
                esriPropertyBoundaryUrl
                    .addParam(ESRI.PARAMS.GEOMETRY, JSON.stringify(geometry))
                    .addParam(ESRI.PARAMS.GEOMETRY_TYPE, ESRI.VALUES.GEOMETRY_TYPE.ESRI_GEOMETRY_MULTIPOINT);
            }
            fetchMapData();
        } else {
            resetLayer();
        }

    }, [JSON.stringify(props.selectOptions?.coordinates)]);

    useEffect(() => {
        if (props.overlayOptions?.events?.click) {
            listeners.current?.remove();
            listeners.current = props.map?.data?.addListener('click', props.overlayOptions.events.click);
        }
    }, [props.overlayOptions?.events?.click]);

    useEffect(() => {
        if (props.selectOptions?.ids?.length) {
            isSingleCoordinate.current = false;
            esriPropertyBoundaryUrl
                .addParam(ESRI.PARAMS.WHERE, `property_id IN (${props.selectOptions.ids.join(',')})`) // limit the property boundaries to be displayed
            fetchMapData();
        } else {
            resetLayer();
        }
    }, [JSON.stringify(props.selectOptions?.ids)]);

    useEffect(() => {
        if (props.selectOptions?.boundingBox) {
            isSingleCoordinate.current = false;
            esriPropertyBoundaryUrl
                .addParam(ESRI.PARAMS.GEOMETRY, `${props.selectOptions.boundingBox.west},${props.selectOptions.boundingBox.south},${props.selectOptions.boundingBox.east},${props.selectOptions.boundingBox.north}`)
                .addParam(ESRI.PARAMS.GEOMETRY_TYPE, ESRI.VALUES.GEOMETRY_TYPE.ESRI_GEOMETRY_ENVELOPE);
            fetchMapData();
        } else {
            resetLayer();
        }
    }, [JSON.stringify(props.selectOptions?.boundingBox)]);

    useEffect(() => {
        if (props.selectOptions?.polygon) {
            isSingleCoordinate.current = false;
            // adding first coordinate to the last coordinate to close the polygon
            const geometry = {
                rings: [props.selectOptions.polygon
                    .concat([props.selectOptions.polygon[0]])
                    .map((latLng) => {
                        return [latLng.lon, latLng.lat];
                    })],
            };
            esriPropertyBoundaryUrl
                .addParam(ESRI.PARAMS.GEOMETRY, JSON.stringify(geometry))
                .addParam(ESRI.PARAMS.GEOMETRY_TYPE, ESRI.VALUES.GEOMETRY_TYPE.ESRI_GEOMETRY_POLYGON);
            fetchMapData();
        } else {
            resetLayer();
        }
    }, [props.selectOptions?.polygon]);

    useEffect(() => { // polyline
        if (props.selectOptions?.polyline) {
            if (!Commons.isEqual(props.selectOptions?.polyline, prevPolyRef.current)) {
                isSingleCoordinate.current = false;
                prevPolyRef.current = props.selectOptions.polyline
                const geometry = {
                    paths: [props.selectOptions.polyline
                        .map((latLng) => {
                            return [latLng.lon, latLng.lat];
                        })]
                }
                esriPropertyBoundaryUrl
                    .addParam(ESRI.PARAMS.GEOMETRY, JSON.stringify(geometry))
                    .addParam(ESRI.PARAMS.GEOMETRY_TYPE, ESRI.VALUES.GEOMETRY_TYPE.ESRI_GEOMETRY_POLYLINE);
                fetchMapData();
            }
        } else {
            resetLayer();
        }
    }, [props.selectOptions?.polyline]);

    useEffect(() => {
        if (props.selectOptions?.circle) {
            isSingleCoordinate.current = false;
            esriPropertyBoundaryUrl
                .addParam(ESRI.PARAMS.DISTANCE, props.selectOptions.circle.radius)
                .addParam(ESRI.PARAMS.GEOMETRY, `${props.selectOptions.circle.center.lon},${props.selectOptions.circle.center.lat}`)
                .addParam(ESRI.PARAMS.GEOMETRY_TYPE, ESRI.VALUES.GEOMETRY_TYPE.ESRI_GEOMETRY_POINT);
            fetchMapData();
        } else {
            resetLayer();
        }
    }, [props.selectOptions?.circle]);

    useEffect(() => {
        removeDataLayer();
        props.map?.data?.revertStyle();
        props.map?.data?.setStyle({
            fillOpacity: 0.08,
            fillColor: Colors.PRIMARY.MAIN,
            strokeColor: Colors.PRIMARY.MAIN,
            strokeWeight: 2,
        });
    }, [props.map?.data]);
};

usePropertyBoundaryLayer.propTypes = {
    map: PropTypes.shape({
        data: PropTypes.shape({
            revertStyle: PropTypes.func,
            setStyle: PropTypes.func,
            loadGeoJson: PropTypes.func,
            remove: PropTypes.func,
        }),
    }),
    selectOptions: PropTypes.shape({
        boundingBox: PropTypes.shape({
            north: PropTypes.number,
            east: PropTypes.number,
            west: PropTypes.number,
            south: PropTypes.number,
        }),
        polygon: PropTypes.arrayOf(PropTypes.shape({
            lat: PropTypes.number,
            lon: PropTypes.number,
        })),
        polyline: PropTypes.arrayOf(PropTypes.shape({
            lat: PropTypes.number,
            lon: PropTypes.number,
        })),
        circle: PropTypes.shape({
            radius: PropTypes.number,
            center: PropTypes.shape({
                lat: PropTypes.number,
                lon: PropTypes.number,
            })
        }),
        coordinates: PropTypes.arrayOf(PropTypes.shape({
            lat: PropTypes.number,
            lon: PropTypes.number,
        })),
        ids: PropTypes.arrayOf(PropTypes.number),
    }),
    overlayOptions: PropTypes.shape({
        latestOnly: PropTypes.bool,
        showPropertyBoundariesOnLoad: PropTypes.bool,
        withPropertyIdOnly: PropTypes.bool,
        onFetchSuccess: PropTypes.func,
        handleOverlayOnFetchError: PropTypes.func,
        events: PropTypes.shape({
            click: PropTypes.func,
        })
    }),
};

export default usePropertyBoundaryLayer;
